import { Tile, type DoorDirection, type DoorPosition } from './Labyrinth';

export interface LoadedMap {
  width: number;
  height: number;
  tiles: Tile[][];
  baseTile: { x: number; y: number };
  doors: DoorPosition[];
}

/**
 * Loads an SVG map and converts it to a tile grid.
 * Convention:
 *   - Black (#000000 or very dark) = Wall
 *   - White (#FFFFFF or bright) = Floor
 *   - Green (high G, low R/B) = Base
 *   - Red (high R, low G/B) = Door
 *
 * Each pixel in the rendered SVG = 1 tile.
 */
export async function loadMapFromSVG(svgUrl: string): Promise<LoadedMap> {
  // Load SVG as an image
  const img = await loadImage(svgUrl);
  const width = img.naturalWidth;
  const height = img.naturalHeight;

  // Render to offscreen canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, width, height);

  // Sample pixels
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  const tiles: Tile[][] = [];
  let baseSumX = 0, baseSumY = 0, baseCount = 0;
  const doorTiles: { x: number; y: number }[] = [];

  for (let y = 0; y < height; y++) {
    tiles[y] = [];
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = pixels[i]!;
      const g = pixels[i + 1]!;
      const b = pixels[i + 2]!;
      const row = tiles[y]!;

      if (r > 200 && g < 100 && b < 100) {
        // Red = door
        row[x] = Tile.Door;
        doorTiles.push({ x, y });
      } else if (g > 200 && r < 100 && b < 100) {
        // Green = base
        row[x] = Tile.Base;
        baseSumX += x;
        baseSumY += y;
        baseCount++;
      } else if (r > 128 && g > 128 && b > 128) {
        // Bright = floor
        row[x] = Tile.Floor;
      } else {
        // Dark = wall
        row[x] = Tile.Wall;
      }
    }
  }

  // Compute base center as centroid of all base tiles
  const baseTile = baseCount > 0
    ? { x: Math.round(baseSumX / baseCount), y: Math.round(baseSumY / baseCount) }
    : { x: Math.floor(width / 2), y: Math.floor(height / 2) };

  // Classify door tiles by direction relative to base center
  const doors: DoorPosition[] = doorTiles.map(dt => {
    const dx = dt.x - baseTile.x;
    const dy = dt.y - baseTile.y;
    let direction: DoorDirection;
    if (Math.abs(dx) > Math.abs(dy)) {
      direction = dx < 0 ? 'west' : 'east';
    } else {
      direction = dy < 0 ? 'north' : 'south';
    }
    return { direction, x: dt.x, y: dt.y };
  });

  return { width, height, tiles, baseTile, doors };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
